###
  DraggyView abstracts dragging functionality across all Thomas's
  templates. It can be bound to a rectangle, locked on an axis or constrained
  to a cicle. It's important to note that DraggyView doesn't actually
  transform the element, it merely provides evented feedback of it's
  interaction. Listen to it's drag and drop events and manipulate the view as
  necessary, whether it be transforming a child, the view itself or adding
  dropping logic rules.

  Properties:
    draggy.x: Current x position
    draggy.y: Current y position
    draggy.t: Current rotation (theta) adjusted to the *top of the circle*

    These attributes are also available as data attributes on draggy.el.

    draggy.velocity:
      x: horizontal pixel movement per ms
      y: vertical pixel movement per ms
      t: angle of movement
      dir: "up", "right", "down", "left" or "none"

    Velocity is a measurement of movement occuring in the last 300ms.
    Use getVelocity(ms) to include movement over a different duration.

  Public methods:
    draggy.lock()
    draggy.unlock()
    draggy.isWithin(bounds[, buffer])
    draggy.isOver(bounds[, buffer])
    draggy.closest([bounds...])
    draggy.distanceTo(x, y[, opts])
    draggy.reset({x, y, t})
    draggy.getVelocity([ms])
    draggy.resetPosition()

  Events:
    drag: (draggy, isFirstDrag)
    drop: (draggy)

  Options:
    minX: Minimum x position
    maxX: Maximum x position
    minY: Minimum y position
    maxY: Maximum y position
    lock: "x" or "y"
    radius: Radius of circular bounds
    isParent: False if the element transforms, true if a child will instead
    allowPropagation: Stop immediate event propagation on touch start
###

Easie                   = require("lib/easie")
Prefix                  = require("lib/prefix")
{ getBounds }           = require("lib/element")
{ events: { pointer } } = require("lib/device")


QUARTER_TURN = Math.PI / 2
FULL_TURN = Math.PI * 2

class DraggyView extends Backbone.View

  initialize: (@options) ->
    @options.minX ?= -Infinity
    @options.minY ?= -Infinity
    @options.maxX ?=  Infinity
    @options.maxY ?=  Infinity

    @x = @el.dataset?.x = 0
    @y = @el.dataset?.y = 0

    @velocity = {}

    @getOffset()

  events: ->
    events =
      "dragstart": "preventDefault"

    events[pointer.start] = "onStart"
    return events

  bindExtra: ->
    document.addEventListener(pointer.move, this, false)
    document.addEventListener(pointer.end, this, false)

  unbindExtra: ->
    document.removeEventListener(pointer.move, this, false)
    document.removeEventListener(pointer.end, this, false)

  handleEvent: (e) ->
    switch e.type
      when pointer.move
        e.preventDefault()
        @onMove(e)
        break
      when pointer.end
        @onEnd(e)
        break

  undelegateEvents: ->
    @unbindExtra()
    super

  onStart: (e) ->
    return if @locked

    e.stopImmediatePropagation() unless @options.allowPropagation

    @el.classList.add("active")
    @el.style.zIndex = 99
    @el.style[Prefix "touchAction"] = "none"

    @history = []

    @getOffset()
    @getStart(e)
    @onMove(e)
    @bindExtra()

  onMove: (e) ->
    e.preventDefault()
    _e = if pointer.isTouch then e.touches[0] else e

    {@x, @y, @t} = @constrain
      x: _e.pageX - @start.x
      y: _e.pageY - @start.y
      buffer: 20

    @getVelocity()

    @trigger("drag", this, e.type is pointer.start)

    @disableUserSelect()

  disableUserSelect: ->
    document.body.classList.add("disable-select")

  onEnd: (e) ->
    @unbindExtra()

    {@x, @y, @t} = @constrain({@x, @y, @t, buffer: 0})

    @el.dataset.x = @x
    @el.dataset.y = @y
    @el.dataset.t = @t

    @el.classList.remove("active")
    @el.style.zIndex = ""
    @el.style[Prefix "touchAction"] = ""

    @trigger("drop", this)
    document.body.classList.remove("disable-select")

  # `getBoundingClientRect` returns an immutable object, so it's necessary to
  # get and set attributes. We'll also subtract the current position if the
  # draggy isn't a parent (and has transforms directly applied)
  getOffset: ->
    {top, left, right, bottom, width, height} = @el.getBoundingClientRect()

    @offset = getBounds @el,
      x: (if @options.isParent then 0 else -@x)
      y: (if @options.isParent then 0 else -@y)

  getStart: (e) ->
    _e = if pointer.isTouch then e.touches[0] else e

    @start =
      x: _e.pageX - @x
      y: _e.pageY - @y

    if @options.isParent
      @start.x += @x + @offset.left - _e.pageX
      @start.y += @y + @offset.top  - _e.pageY

    if @options.radius
      @start.x += @options.radius
      @start.y += @options.radius

  getVelocity: (ms = 300) ->
    now = Date.now()

    @history.push { @x, @y, timestamp: now }

    history = @history.filter (p) -> now - p.timestamp < ms

    [x, y] =
      for dimension in ["x", "y"]
        velocity = history.reduce ((m, p1, i, a) ->
          p2 = a[i - 1] or p1
          m + (p2[dimension] - p1[dimension])
        ), 0

        velocity / (now - history[0].timestamp)

    t   = -Math.atan2(x, y)

    dir =
      switch Math.round(t / QUARTER_TURN)
        when     0 then "up"
        when     1 then "right"
        when 2, -2 then "down"
        when    -1 then "left"
        else "none"

    @velocity = { x, y, t, dir }

  constrain: ({x, y, buffer}) ->
    {minX, minY, maxX, maxY, lock, radius} = @options

    if radius
      t = @toAngle(x, y)
      { x, y } = @toPosition(t)

    else
      x = if lock is "x" then 0 else @limit(x, minX, maxX, buffer)
      y = if lock is "y" then 0 else @limit(y, minY, maxY, buffer)

    return {x, y, t}

  limit: (value, min, max, buffer) ->
    if value < min
      min - Easie.expoOut(min - value, 0, buffer, max - min)
    else if value > max
      max + Easie.expoOut(value - max, 0, buffer, max - min)
    else
      value

  setPosition: ({x, y, t} = {}) ->
    if x? or y? or not t?
      x ?= 0
      y ?= 0
      t = @toAngle(x, y)
      t += FULL_TURN if t < 0
    else if t?
      { x, y } = @toPosition(t)

    @el.dataset.t = @t = t
    @el.dataset.x = @x = x
    @el.dataset.y = @y = y 
    
  reset: ({x, y, t} = {}) ->
    @setPosition({x, y, t})
    @trigger("drop", this, true)

  resetPosition: ->
    @el.dataset.t = @t = 0
    @el.dataset.x = @x = 0
    @el.dataset.y = @y = 0

  toAngle: (x, y) ->
    -Math.atan2(x, y) + Math.PI

  toPosition: (t) ->
    x: Math.cos(t - QUARTER_TURN) * @options.radius
    y: Math.sin(t - QUARTER_TURN) * @options.radius

  lock: ->
    @locked = true
    @el.classList.add("locked")

  unlock: ->
    @locked = false
    @el.classList.remove("locked")

  isWithin: (bounds, buffer = 0) ->
    if @options.isParent
      left = top = right = bottom = 0
    else
      { left, top, right, bottom } = @offset

    left   + @x > bounds.left   - buffer and
    right  + @x < bounds.right  + buffer and
    top    + @y > bounds.top    - buffer and
    bottom + @y < bounds.bottom + buffer

  isOver: (bounds, buffer) ->
    { left, top, right, bottom } = bounds
    { width, height } = @offset

    left   -= width
    right  += width
    top    -= height
    bottom += height

    @isWithin { left, top, right, bottom }, buffer

  closest: (areas) ->
    a =
      xmin: @offset.left + @x
      xmax: @offset.left + @x + @offset.width
      ymin: @offset.top + @y
      ymax: @offset.top + @y + @offset.height

    _.chain(areas)
      .map (bounds) ->
        xmin: bounds.left
        xmax: bounds.left + bounds.width
        ymin: bounds.top
        ymax: bounds.top + bounds.height

      .map (b, index) ->
        dx = Math.min(a.xmax, b.xmax) - Math.max(a.xmin, b.xmin)
        dy = Math.min(a.ymax, b.ymax) - Math.max(a.ymin, b.ymin)
        if dx >= 0 and dy >= 0
          area = dx * dy

        { area, index }

      .filter("area")
      .sortBy("area")
      .last()
      .value()
      ?.index

  distanceTo: (x1, y1, {fromCenter, offsetX, offsetY} = {}) ->
    {left, top, width, height} = @offset

    offsetX ?= if fromCenter then left + width / 2 else 0
    offsetY ?= if fromCenter then top + height / 2 else 0

    x = x1 - (@x + offsetX)
    y = y1 - (@y + offsetY)

    Math.sqrt(x * x + y * y)

  preventDefault: ->
    false

# This draggy class is for templates who need to deal with the cursor sticky problem
class AutoCursorRemoveDraggy extends DraggyView 
  bindExtra: ->
    super
    document.addEventListener('mouseleave', this, false)

  unbindExtra: ->
    super
    document.removeEventListener('mouseleave', this, false)

  handleEvent: (e) ->
    super
    if e.type is 'mouseleave'
      @onEnd(e)

module.exports = { DraggyView, AutoCursorRemoveDraggy }
